import { useEffect, useRef } from "react";
import { useQuestionnaire } from "../../../../../../../features/questionnaire";
import type { AnswerChangeEvent } from "../../types";
import { BUSINESS_CITY_KEY } from "../../../../../../../constants/constants";

interface UseUpdateCityParams {
  city: string | undefined;
  isValid: boolean;
}

/**
 * Creates a properly typed custom answer change event.
 */
function createAnswerChangeEvent(
  name: string,
  value: string,
): AnswerChangeEvent {
  return {
    target: {
      name,
      value,
    },
  };
}

/**
 * Hook to automatically update the business city answer when the city value changes.
 * This is typically used with zip code validation to populate the business city field
 * based on the validated zip code.
 * Only updates the business city when the validation is valid.
 */
export const useUpdateCity = ({ city, isValid }: UseUpdateCityParams) => {
  const { updateAnswer } = useQuestionnaire();
  const previousCityRef = useRef<string | undefined>(undefined);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip the first render to avoid updating with initial values
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousCityRef.current = city;
      return;
    }

    const hasCityChanged = previousCityRef.current !== city;

    if (hasCityChanged && city && isValid) {
      updateAnswer(createAnswerChangeEvent(BUSINESS_CITY_KEY, city));
    }

    // Update the ref after checking for changes
    previousCityRef.current = city;
  }, [city, isValid, updateAnswer]);
};
